// Какую карточку объекта карты показывать: наведённую или закреплённую кликом.
// Наведение — «читалка»: живёт, пока курсор над точкой, и ничего не перекрывает
// (карточка не ловит мышь, иначе она гасила бы сама себя). Клик закрепляет:
// закреплённая карточка держится до крестика и НЕ подменяется наведением на
// соседний объект — иначе её нельзя было бы дочитать, ведя курсор к крестику.
// Логика общая для всех кликабельных слоёв (суда, косяки, снесённые точки):
// карточки независимы, закреплённые сосуществуют на карте одновременно.

/** Где стоит САМ объект + свойства его фичи.
 *
 *  Координаты, а не пиксели курсора: карточка привязана к точке на карте и
 *  переезжает вместе с ней (см. `MapCallout`), а пиксель клика перестаёт что-то
 *  значить, как только карту сдвинули. */
export interface MapPick {
  lng: number
  lat: number
  props: Record<string, unknown>
}

export function activeCard<T>(
  pinned: T | null,
  hovered: T | null,
): { card: T; pinned: boolean } | null {
  if (pinned) return { card: pinned, pinned: true }
  if (hovered) return { card: hovered, pinned: false }
  return null
}

// --- личность объекта в карточке ------------------------------------------
//
// «Тот же самый объект или другой» — вопрос о ТОЧКЕ, а не об адресе курсора на
// экране. Пока этим служили пиксели (`key={`${x},${y}`}` у карточки), каждое
// дрожание мыши означало «объект другой»: React сносил узел и создавал новый —
// замер на стенде дал 108 пар «снести — создать» за 14 секунд наведения. Ровно
// на этом кабинет и сыпался: в Safari при загрузке часть сносов не доезжает
// (замер владельца: 129 добавлений против 113 удалений за одно переключение
// раздела), и каждый несостоявшийся снос оставлял карточку висеть над картой —
// набиралось по три десятка.
//
// Поэтому ключа у карточек больше нет вовсе: узел живёт один и обновляется на
// месте, сносить нечего. А сбросить своё внутреннее состояние («скопировано»,
// ход обновления судна) карточке всё равно надо при СМЕНЕ ОБЪЕКТА — вот на этот
// вопрос и отвечают функции ниже.

/** Личность снесённой точки: источник + номер отметки (`ref` уникален лишь
 *  внутри источника — вылов №12 и косяк №12 разные). Номера нет у старых срезов
 *  — тогда координаты САМОЙ ТОЧКИ, которые от движения мыши не зависят. */
export function cardKey(card: {
  lng: number
  lat: number
  props?: Record<string, unknown>
}): string {
  const source = String(card.props?.source ?? 'drift')
  const ref = card.props?.ref
  return ref == null ? `${source}@${card.lng},${card.lat}` : `${source}#${ref}`
}

/** Личность судна: MMSI. Без него — позиция судна (не курсора). */
export function vesselKey(card: { props?: Record<string, unknown> }): string {
  const mmsi = card.props?.mmsi
  if (mmsi != null && mmsi !== '') return `mmsi#${mmsi}`
  return `vessel@${card.props?.lon},${card.props?.lat}`
}

/** Читалку косяка гасим, если по этому же косяку уже открыта форма правки
 *  (два окна об одном объекте в одной точке — шум, а форма полнее), а также
 *  если под курсором лежит снесённая точка: у клика приоритет тот же — снос
 *  главнее отметки, и наведение не должно обещать другое. */
export function clusterPeek<T extends { id: number }>(
  hovered: T | null,
  editingId: number | null,
  driftHovered = false,
): T | null {
  if (!hovered || driftHovered) return null
  return hovered.id === editingId ? null : hovered
}

// --- кто рисует отметку и куда ведёт клик по ней --------------------------
//
// Отметку происхождения теперь рисует слой СНОСА — одинаково для косяка и для
// факта вылова (раньше её умел только слой `clusters`, и вылов оставался
// одинокой точкой посреди трека). Отсюда два следствия, которые надо развести
// явно, иначе рефактор ломает рабочее взаимодействие с косяками.

/** Куда ведёт клик по маркеру слоя сноса.
 *
 * Отметка косяка сменила слой, но не смысл: по ней по-прежнему открывается
 * форма правки. Снесённые маркеры (`day`/`today`) правке не подлежат — их
 * координаты посчитаны движком, а не введены человеком. Донесение о вылове с
 * карты не правится вовсе. */
export function driftClickTarget(hit: {
  source: string
  role: string
}): 'cluster-edit' | 'drift-card' {
  return hit.source === 'cluster' && hit.role === 'origin' ? 'cluster-edit' : 'drift-card'
}

/** Косяки, отметку которых слой `clusters` рисует сам — то есть те, у кого
 *  трека сноса нет. У остальных отметку рисует слой сноса, и второй кружок в
 *  той же точке был бы просто грязью.
 *
 *  Сверяем по `source === 'cluster'`: `ref` уникален только внутри источника,
 *  и вылов с тем же номером погасил бы чужую отметку. */
export function clustersWithoutTrack<T extends { id: number }>(
  clusters: T[],
  drift: { features: Array<{ properties: { source?: string; ref?: number } }> } | null,
): T[] {
  if (!drift) return clusters
  const drifting = new Set<number>()
  for (const f of drift.features) {
    if (f.properties?.source === 'cluster' && f.properties.ref != null) {
      drifting.add(f.properties.ref)
    }
  }
  return clusters.filter(c => !drifting.has(c.id))
}

// --- закреплённая карточка судна и свежий слой -----------------------------

/**
 * Догнать закреплённую карточку судна свежим слоем флота.
 *
 * Карточка держит СНИМОК свойств, снятый в миг клика, и это правильно: слой
 * режется вьюпортом, и читать из него на каждом кадре означало бы терять
 * карточку от любого движения карты. Но у снимка есть цена, и её предъявил бой
 * 06.09.2026: кнопка «Обновить данные» отработала (задания 433 и 434,
 * `updated=1` — апсерт с guard'ом пропускает только фикс СВЕЖЕЕ нашего, то есть
 * позиция действительно поехала), судно на карте переехало, а карточка осталась
 * со вчерашним «последняя позиция 5 сут назад». Наружу это неотличимо от
 * сломанной кнопки.
 *
 * Правила:
 *   * сводим ТОЛЬКО по MMSI. Он и есть личность судна (`vesselKey`); сведение
 *     по близости координат подставило бы в карточку соседнее судно;
 *   * судна нет в свежем наборе — карточка остаётся КАК БЫЛА. Слой режется
 *     вьюпортом, и погасив её, мы закрыли бы карточку человеку под руками за
 *     то, что он сдвинул карту;
 *   * ничего не изменилось — возвращаем ТОТ ЖЕ объект. Слой перезапрашивается
 *     на каждом пане и раз в час; новая ссылка на каждый ответ означала бы
 *     перерисовку карточки без единой причины.
 */
export function refreshedPick(
  pick: MapPick | null,
  features: readonly GeoJSON.Feature[],
): MapPick | null {
  if (!pick) return null
  const mmsi = pick.props?.mmsi
  if (mmsi == null || mmsi === '') return pick
  const key = String(mmsi)
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>
    if (String(props.mmsi ?? '') !== key) continue
    const geom = f.geometry as GeoJSON.Point | null
    const coords = geom?.type === 'Point' ? geom.coordinates : null
    const lng = typeof coords?.[0] === 'number' ? coords[0] : pick.lng
    const lat = typeof coords?.[1] === 'number' ? coords[1] : pick.lat
    if (lng === pick.lng && lat === pick.lat && samePropsShallow(pick.props, props)) return pick
    return { lng, lat, props }
  }
  return pick
}

/** Свойства фичи — плоский словарь скаляров, поэтому сравнения по значению
 *  хватает. Глубокое сравнение здесь было бы обещанием, которого набор свойств
 *  судна не требует. */
function samePropsShallow(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const ka = Object.keys(a)
  if (ka.length !== Object.keys(b).length) return false
  return ka.every(k => Object.is(a[k], b[k]))
}
